import { useEffect, useMemo, useState } from 'react';
import type { ViberMessageType } from '../components/mailing-constructor/mailingTypes';
import type { SmsCascadePrefetchCache } from './smsCascadePrefetch';
import { getSmsCascadePrefetchCache, prefetchSmsCascadeContext } from './smsCascadePrefetch';

export type SmsAlphaRow = { id: number; name: string };
export type SmsContactListRow = { id: number; name: string; count?: number };

/** Шаблон провайдера: Viber (templateNames + templateMessages) или SMS (templatesMessages — объект по id). */
export type ViberTemplateRow = { id: number; name: string; message?: string };

/** Ответ GET …/viber/check-alphanames. */
export type ViberAlphanameCheck = {
  countAlphanames: number;
  countAlphanamesWithDirections: number;
};

/** Тип сообщения из GET viber/create(-api), поле messageTypes. */
export type ViberMessageKindOption = { id: string; name: string };

export type SmsProviderCatalog = {
  alphanames: SmsAlphaRow[];
  contactLists: SmsContactListRow[];
  countBalance?: number;
  /** SMS: GET sms/create(-api), поле templatesMessages (объект) или пары templateNames + templateMessages. */
  smsTemplates?: ViberTemplateRow[];
  /** Только для Viber (ответ viber/create). */
  viberTemplates?: ViberTemplateRow[];
  /** GET …/viber/check-alphanames (префетч). */
  viberAlphanameCheck?: ViberAlphanameCheck;
  /** Число элементов в fees из GET …/viber/check-fee (префетч). */
  viberFeeCount?: number;
  /** Соединение viber/create + viber/create-api. */
  viberMessageTypes?: ViberMessageKindOption[];
};

/** id из API (TEXT, BUTTON, …) → значение узла канала. */
export function mapViberApiMessageKindToChannelType(kind: string): ViberMessageType {
  const u = kind.trim().toUpperCase();
  if (u === 'TEXT' || u === 'PLAIN') return 'plain';
  if (u === 'BUTTON' || u === 'WITH_BUTTON' || u === 'WITHBUTTON') return 'withButton';
  if (u === 'IMAGE' || u === 'IMG' || u === 'PICTURE') return 'image';
  return 'plain';
}

function pickRecord(obj: unknown): Record<string, unknown> | null {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return obj as Record<string, unknown>;
  }
  return null;
}

function smsRootsFromCache(cache: SmsCascadePrefetchCache): Record<string, unknown>[] {
  return [pickRecord(cache.create), pickRecord(cache.createApi)].filter(
    (x): x is Record<string, unknown> => x != null,
  );
}

/** Объект templatesMessages (vue-sms) и/или массивы templateNames + templateMessages. */
function extractSmsTemplatesFromRoots(roots: Record<string, unknown>[]): ViberTemplateRow[] {
  const byId = new Map<number, ViberTemplateRow>();

  for (const root of roots) {
    const dict = root.templatesMessages;
    if (!dict || typeof dict !== 'object' || Array.isArray(dict)) continue;
    for (const v of Object.values(dict as Record<string, unknown>)) {
      const o = pickRecord(v);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      const message = o.message != null ? String(o.message) : undefined;
      byId.set(id, { id, name, ...(message !== undefined ? { message } : {}) });
    }
  }

  const messageById = new Map<number, string>();
  for (const root of roots) {
    const tmsg = root.templateMessages;
    if (!Array.isArray(tmsg)) continue;
    for (const it of tmsg) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      if (!Number.isFinite(id)) continue;
      const message = o.message != null ? String(o.message) : undefined;
      if (message !== undefined) messageById.set(id, message);
    }
  }

  for (const root of roots) {
    const tnames = root.templateNames;
    if (!Array.isArray(tnames)) continue;
    for (const it of tnames) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      const message = messageById.get(id);
      const row: ViberTemplateRow = { id, name, ...(message !== undefined ? { message } : {}) };
      const prev = byId.get(id);
      if (!prev) byId.set(id, row);
      else if (!prev.message && message !== undefined) byId.set(id, { ...prev, message });
    }
  }

  return [...byId.values()].sort((a, b) => a.id - b.id);
}

/** Совмещает ответы create и create-api: альфа-имена, списки, баланс, шаблоны SMS. */
export function extractSmsCatalog(cache: SmsCascadePrefetchCache): SmsProviderCatalog {
  const alphanames: SmsAlphaRow[] = [];
  const seenAlpha = new Set<number>();

  const pushAlphas = (root: unknown) => {
    const r = pickRecord(root);
    const raw = r?.alphanames;
    if (!Array.isArray(raw)) return;
    for (const it of raw) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      if (seenAlpha.has(id)) continue;
      seenAlpha.add(id);
      alphanames.push({ id, name });
    }
  };

  pushAlphas(cache.create);
  pushAlphas(cache.createApi);

  const roots = smsRootsFromCache(cache);

  const seenList = new Set<number>();
  const contactLists: SmsContactListRow[] = [];
  for (const cr of roots) {
    const lists = cr.contactLists;
    if (!Array.isArray(lists)) continue;
    for (const it of lists) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      if (seenList.has(id)) continue;
      seenList.add(id);
      const countRaw = o.count;
      const count = typeof countRaw === 'number' ? countRaw : undefined;
      contactLists.push({ id, name, count });
    }
  }

  let countBalance: number | undefined;
  const crCreate = pickRecord(cache.create);
  const balCreate = crCreate?.countBalance;
  if (typeof balCreate === 'number' && Number.isFinite(balCreate)) {
    countBalance = balCreate;
  } else {
    const crApi = pickRecord(cache.createApi);
    const balApi = crApi?.countBalance;
    if (typeof balApi === 'number' && Number.isFinite(balApi)) countBalance = balApi;
  }

  const smsTemplates = extractSmsTemplatesFromRoots(roots);

  return {
    alphanames,
    contactLists,
    countBalance,
    ...(smsTemplates.length > 0 ? { smsTemplates } : {}),
  };
}

function pushAlphasFromRoot(root: Record<string, unknown> | null, key: string, out: SmsAlphaRow[], seen: Set<number>) {
  if (!root) return;
  const raw = root[key];
  if (!Array.isArray(raw)) return;
  for (const it of raw) {
    const o = pickRecord(it);
    if (!o) continue;
    const id = Number(o.id);
    const name = String(o.name ?? '').trim();
    if (!Number.isFinite(id) || !name) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name });
  }
}

function parseViberFeeCount(cache: SmsCascadePrefetchCache): number | undefined {
  const root = pickRecord(cache.viberCheckFee);
  if (!root) return undefined;
  const fees = root.fees;
  if (!Array.isArray(fees)) return undefined;
  return fees.length;
}

function parseViberAlphanameCheck(cache: SmsCascadePrefetchCache): ViberAlphanameCheck | undefined {
  const chk = pickRecord(cache.viberCheckAlphanames);
  if (!chk) return undefined;
  const ca = chk.countAlphanames;
  const cwd = chk.countAlphanamesWithDirections;
  if (typeof ca !== 'number' || !Number.isFinite(ca)) return undefined;
  if (typeof cwd !== 'number' || !Number.isFinite(cwd)) return undefined;
  return { countAlphanames: ca, countAlphanamesWithDirections: cwd };
}

function viberRootsFromCache(cache: SmsCascadePrefetchCache): Record<string, unknown>[] {
  return [pickRecord(cache.viberCreate), pickRecord(cache.viberCreateApi)].filter(
    (x): x is Record<string, unknown> => x != null,
  );
}

function parseViberMessageTypes(roots: Record<string, unknown>[]): ViberMessageKindOption[] {
  const seen = new Set<string>();
  const out: ViberMessageKindOption[] = [];
  for (const root of roots) {
    const raw = root.messageTypes;
    if (!Array.isArray(raw)) continue;
    for (const it of raw) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = String(o.id ?? '').trim();
      const name = String(o.name ?? '').trim();
      if (!id || !name) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name });
    }
  }
  return out;
}

/** Справочники Viber: объединение GET viber/create и viber/create-api (как SMS create + create-api). */
export function extractViberCatalog(cache: SmsCascadePrefetchCache): SmsProviderCatalog {
  const viberAlphanameCheck = parseViberAlphanameCheck(cache);
  const viberFeeCount = parseViberFeeCount(cache);
  const roots = viberRootsFromCache(cache);
  const viberMessageTypes = parseViberMessageTypes(roots);

  if (roots.length === 0) {
    return {
      alphanames: [],
      contactLists: [],
      countBalance: undefined,
      viberTemplates: [],
      viberAlphanameCheck,
      viberFeeCount,
      viberMessageTypes,
    };
  }

  const seenAlpha = new Set<number>();
  const alphanames: SmsAlphaRow[] = [];
  for (const r of roots) {
    pushAlphasFromRoot(r, 'viberAlphanames', alphanames, seenAlpha);
    pushAlphasFromRoot(r, 'viber_alphanames', alphanames, seenAlpha);
  }
  /** Не подставляем `alphanames` от viber/create — это список SMS-отправителей с того же типа ответа. */

  const seenList = new Set<number>();
  const contactLists: SmsContactListRow[] = [];
  for (const r of roots) {
    const lists = r.contactLists;
    if (!Array.isArray(lists)) continue;
    for (const it of lists) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      if (seenList.has(id)) continue;
      seenList.add(id);
      const countRaw = o.count;
      const count = typeof countRaw === 'number' ? countRaw : undefined;
      contactLists.push({ id, name, count });
    }
  }

  let countBalance: number | undefined;
  const crCreate = pickRecord(cache.viberCreate);
  const balCreate = crCreate?.countBalance;
  if (typeof balCreate === 'number' && Number.isFinite(balCreate)) {
    countBalance = balCreate;
  } else {
    const crApi = pickRecord(cache.viberCreateApi);
    const balApi = crApi?.countBalance;
    if (typeof balApi === 'number' && Number.isFinite(balApi)) countBalance = balApi;
  }

  const messageById = new Map<number, string>();
  for (const r of roots) {
    const tmsg = r.templateMessages;
    if (!Array.isArray(tmsg)) continue;
    for (const it of tmsg) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      if (!Number.isFinite(id)) continue;
      const message = o.message != null ? String(o.message) : undefined;
      if (message !== undefined) messageById.set(id, message);
    }
  }

  const seenTpl = new Set<number>();
  const viberTemplates: ViberTemplateRow[] = [];
  for (const r of roots) {
    const tnames = r.templateNames;
    if (!Array.isArray(tnames)) continue;
    for (const it of tnames) {
      const o = pickRecord(it);
      if (!o) continue;
      const id = Number(o.id);
      const name = String(o.name ?? '').trim();
      if (!Number.isFinite(id) || !name) continue;
      if (seenTpl.has(id)) continue;
      seenTpl.add(id);
      const message = messageById.get(id);
      viberTemplates.push({ id, name, ...(message !== undefined ? { message } : {}) });
    }
  }

  return {
    alphanames,
    contactLists,
    countBalance,
    viberTemplates,
    viberAlphanameCheck,
    viberFeeCount,
    viberMessageTypes,
  };
}

/** Значение для Select: совпадение по id или по имени (старые данные). */
export function resolveAlphaSelectValue(senderName: string, rows: SmsAlphaRow[]): string {
  const s = senderName.trim();
  if (!s) return '';
  if (rows.some((r) => String(r.id) === s)) return s;
  const byName = rows.find((r) => r.name === s);
  return byName ? String(byName.id) : '';
}

export function resolveContactListSelectValue(contactListQuery: string, rows: SmsContactListRow[]): string {
  const s = contactListQuery.trim();
  if (!s) return '';
  if (rows.some((r) => String(r.id) === s)) return s;
  const byName = rows.find((r) => r.name === s);
  return byName ? String(byName.id) : '';
}

export function useSmsProviderCatalog(active: boolean): SmsProviderCatalog {
  const [rev, setRev] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void prefetchSmsCascadeContext().then(() => {
      if (!cancelled) setRev((x) => x + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return useMemo(() => {
    if (!active) {
      return { alphanames: [], contactLists: [], countBalance: undefined };
    }
    return extractSmsCatalog(getSmsCascadePrefetchCache());
  }, [active, rev]);
}

export function useViberProviderCatalog(active: boolean): SmsProviderCatalog {
  const [rev, setRev] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void prefetchSmsCascadeContext().then(() => {
      if (!cancelled) setRev((x) => x + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return useMemo(() => {
    if (!active) {
      return { alphanames: [], contactLists: [], countBalance: undefined, viberTemplates: [], viberAlphanameCheck: undefined, viberFeeCount: undefined, viberMessageTypes: [] };
    }
    return extractViberCatalog(getSmsCascadePrefetchCache());
  }, [active, rev]);
}
